import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ClipboardCopy } from "lucide-react";
import successSound from "../assets/sounds/success.mp3";
import errorSound from "../assets/sounds/failed.mp3";

// Props for modal status and control
interface TransactionModalProps {
  txStatus: "pending" | "confirmed" | "failed" | null; // Current transaction state
  txHash: string | null; // Transaction hash to display
  closeModal: () => void; // Function to close the modal
  userAddress: string | null; // User's wallet address (optional, can be used for logging)
}

const TransactionModal: React.FC<TransactionModalProps> = ({
  txStatus,
  txHash,
  closeModal,
}) => {
  const [copied, setCopied] = useState(false); // Clipboard copy feedback flag

  // Auto-close modal after confirmation or failure
  useEffect(() => {
    if ((txStatus === "confirmed" || txStatus === "failed") && txHash) {
      const timer = setTimeout(closeModal, 5000);
      return () => clearTimeout(timer);
    }
  }, [txStatus, txHash, closeModal]);

  // Play sound on status change
  useEffect(() => {
    if (txStatus === "confirmed") new Audio(successSound).play();
    else if (txStatus === "failed") new Audio(errorSound).play();
  }, [txStatus]);

  // Copy transaction hash to clipboard
  const copyHash = () => {
    if (!txHash) return;
    navigator.clipboard.writeText(txHash);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Icon based on transaction status
  const renderIcon = () => {
    switch (txStatus) {
      case "pending":
        return <Loader2 className="w-10 h-10 animate-spin text-blue-400" />;
      case "confirmed":
        return (
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 1] }}
            transition={{ duration: 0.6 }}
          >
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </motion.div>
        );
      case "failed":
        return (
          <motion.div
            initial={{ x: 0 }}
            animate={{ x: [-5, 5, -3, 3, 0] }}
            transition={{ duration: 0.4 }}
          >
            <XCircle className="w-10 h-10 text-red-400" />
          </motion.div>
        );
      default:
        return null;
    }
  };

  // Message based on transaction status
  const renderMessage = () => {
    switch (txStatus) {
      case "pending":
        return (
          <p className="text-blue-300 font-semibold">Transaction pending...</p>
        );
      case "confirmed":
        return (
          <p className="text-green-400 font-semibold">Transaction confirmed!</p>
        );
      case "failed":
        return <p className="text-red-400 font-semibold">Transaction failed</p>;
      default:
        return null;
    }
  };

  // Link to block explorer if hash exists
  const explorerLink = txHash ? `https://basescan.org/tx/${txHash}` : undefined;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg p-6 w-full max-w-sm text-white shadow-lg flex flex-col items-center gap-4 overflow-y-auto"
        initial={{ scale: 0.9, y: -10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 10 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Close button */}
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 text-white/60 hover:text-white text-lg"
          aria-label="Close modal"
        >
          ✕
        </button>

        {/* Auto-shrinking progress bar for confirmed/failed */}
        {(txStatus === "confirmed" || txStatus === "failed") && (
          <motion.div
            className="absolute top-0 left-0 h-1 bg-[var(--color-primary-light)] rounded-tr-lg rounded-tl-lg"
            initial={{ width: "100%" }}
            animate={{ width: 0 }}
            transition={{ duration: 10, ease: "linear" }}
          />
        )}

        {renderIcon()}
        {renderMessage()}

        {/* Fallback message before any status */}
        {!txStatus && !txHash && (
          <p className="text-white/50 text-xs">
            Waiting for transaction result...
          </p>
        )}

        {/* Transaction hash display and copy button */}
        {txHash && (
          <div className="w-full bg-black/20 px-4 py-3 border border-white/20 rounded text-center text-xs flex flex-col gap-2">
            <span className="text-[var(--color-primary-light)] font-semibold">
              Transaction Hash
            </span>

            <a
              href={explorerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono break-words text-green-700 hover:text-green-500 transition"
              title="View on BaseScan"
            >
              {txHash}
            </a>

            {copied ? (
              <button
                disabled
                className="bg-green-600 text-[10px] px-2 py-1 rounded opacity-90 cursor-not-allowed"
              >
                Copied!
              </button>
            ) : (
              <button
                onClick={copyHash}
                className="bg-white/20 hover:bg-white/30 text-[10px] px-2 py-1 rounded flex items-center justify-center gap-1 transition"
              >
                <ClipboardCopy className="w-3 h-3" />
                Copy Hash
              </button>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default TransactionModal;
